import styled, { keyframes } from 'styled-components';

// Modal de drill-down do donut (UCHAs/Bombas por status). Alinhada ao
// design system CCO: tokens var(--cco-*) (mesmas superficies/linhas/tinta
// do AlertOverlay, MarkerDetailPanel e cards do dashboard), em vez do tema
// legado (props.theme.colors.*) que destoava.

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 50;
  animation: ${fadeIn} 180ms ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

export const Panel = styled.div`
  background: var(--cco-surface);
  border: 1px solid var(--cco-line);
  border-radius: 14px;
  width: 100%;
  max-width: 1080px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  animation: ${slideUp} 220ms ease-out;
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--cco-line);
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: var(--cco-ink);
`;

export const TitleDot = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${(props) => props.color};
  flex-shrink: 0;
  box-shadow: 0 0 0 3px
    color-mix(in srgb, ${(props) => props.color} 22%, transparent);
`;

export const Subtitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--cco-ink-faint);
`;

export const CloseButton = styled.button`
  background: transparent;
  border: 1px solid var(--cco-line);
  color: var(--cco-ink-mute);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 150ms, color 150ms, border-color 150ms;

  :hover {
    background: var(--cco-soft-surface);
    border-color: var(--cco-line-strong);
    color: var(--cco-ink);
  }
`;

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 20px;

  /* scroll discreto, igual ao resto do CCO */
  scrollbar-width: thin;
  scrollbar-color: var(--cco-line-strong) transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--cco-line-strong);
    border-radius: 3px;
  }
`;

// Grid de cards — auto-fill minmax 200px, 1fr (mesmo padrao do dashboard).
export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

export const Card = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid var(--cco-line);
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 150ms, border-color 150ms, transform 150ms,
    box-shadow 150ms;
  color: var(--cco-ink);
  min-height: 120px;

  :hover {
    border-color: var(--cco-line-strong);
    background: var(--cco-soft-surface);
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.4);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: 999px;
  background: ${(props) => props.color};
  color: var(--cco-bg, #0f1117);
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

export const ErrorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  background: var(--cco-red);
  color: var(--cco-bg, #0f1117);
  font-size: 11px;
  font-weight: 800;
  border-radius: 9px;
  font-variant-numeric: tabular-nums;
`;

export const CardName = styled.h3`
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--cco-ink);
  word-break: break-word;
`;

export const CardCustomer = styled.span`
  font-size: 11px;
  color: var(--cco-ink-faint);
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const BreakdownRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: auto;
  padding-top: 6px;
`;

export const BreakdownChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  font-size: 10px;
  font-weight: 700;
  color: var(--cco-ink-mute);
  font-variant-numeric: tabular-nums;
`;

export const BreakdownDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => props.color};
`;

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 44px 20px;
  text-align: center;
  color: var(--cco-ink-faint);
  font-size: 13px;

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--cco-line-strong);
  }
`;
